import React from "react";

import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import BaseForm from "../../components/Form/BaseForm";
import StepContainer from "../../components/StepContainer";
import {
  FormFieldConfig,
  Opportunity,
  OpportunityProspectTypes,
} from "../../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../../helpers/utilities";
import { ASSSIGN_COMPANY_QUERY_TO_OPPORTUNITY } from "../../gql/companies";

interface Props {
  setPercentage: (percentage: number) => void;
  getPreviousViewName: () => string | null;
  getNextViewName: (prospect?: string) => string | null;
  opportunity: Opportunity;
  companyName?: string;
  prev: () => void;
  next: () => void;
}

interface State {
  isLoading: boolean;
}

export default class CompanyNameForm extends React.Component<Props, State> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = { isLoading: true };

    this.assignCompanyQueryToOpportunity =
      this.assignCompanyQueryToOpportunity.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  assignCompanyQueryToOpportunity<T>(
    companyName: string,
  ): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSSIGN_COMPANY_QUERY_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        companyName,
      },
    });
  }

  next = async (): Promise<void> => {
    const fieldsToBeValidated = ["companyName"];

    interface Response {
      data: {
        assignCompanyQueryToOpportunity: {
          opportunity: Opportunity;
          errors: [];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    const assignMutation = this.assignCompanyQueryToOpportunity;

    try {
      const response: AxiosResponse<Response> = await assignMutation<Response>(
        data.companyName,
      );

      const { err, handled } = responseHandler<Response>(
        response,
        "assignCompanyQueryToOpportunity",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.props.next();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  generateNextViewName = (): string | null => {
    return this.props.getNextViewName(
      OpportunityProspectTypes.institutionalClient,
    );
  };

  componentDidMount(): void {
    this.props.setPercentage(30);
    this.setState({ isLoading: false });
  }

  render(): JSX.Element {
    const { companyName } = this.props;
    const { isLoading } = this.state;

    const fields: FormFieldConfig[] = [
      {
        id: "companyName",
        name: "companyName",
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Company Name",
        label: "Company Name",
        initialValue: companyName,
        colConfig: {
          span: 9,
        },
      },
    ];
    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.generateNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            prev: this.props.prev,
            next: this.next,
            showNext: true,
            showPrev: true,
          }}
        >
          <BaseForm
            style={{ marginBottom: "9px" }}
            reference={this.reference}
            layout="vertical"
            fieldsToHide={[]}
            fields={fields}
          ></BaseForm>
        </StepContainer>
      </Spin>
    );
  }
}
